import { useEffect, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import {
  loadEsriLeaflet, checkYearService,
  googleMapsProvider, googleSatelliteProvider, googleHybridProvider,
} from '../../MapWithDrawing/Measure/mapUtils';

// ===== GIS Overlays definitions (matches V1) =====
export const GIS_OVERLAYS = [
  {
    key: 'contours',
    label: 'Topographic (10ft Contours)',
    type: 'dynamic',
    url: 'https://gis.maricopa.gov/arcgis/rest/services/GIO/Countywide10FootContour/MapServer',
    opacity: 1.0,
  },
  {
    key: 'flood',
    label: 'FEMA Flood Zones',
    type: 'dynamic',
    url: 'https://gis.maricopa.gov/arcgis/rest/services/CompositeService/FloodDataFEMA/MapServer',
    opacity: 0.5,
  },
  {
    key: 'parcels',
    label: 'Parcel Outlines',
    type: 'feature',
    url: 'https://gis.mcassessor.maricopa.gov/arcgis/rest/services/Parcels/MapServer/0',
    style: { color: '#FF3D00', weight: 3, fillOpacity: 0, opacity: 1 },
  },
  {
    key: 'parcelLabels',
    label: 'Parcel Labels (APN)',
    type: 'dynamic',
    url: 'https://gis.mcassessor.maricopa.gov/arcgis/rest/services/ParcelLabel/MapServer',
    opacity: 0.8,
  },
  {
    key: 'streets',
    label: 'Streets',
    type: 'dynamic',
    url: 'https://gis.maricopa.gov/arcgis/rest/services/IndividualService/Street/MapServer',
    opacity: 0.6,
  },
];

const LS_KEY_BASEMAP = 'siteTakeoffV2_basemap';
const LS_KEY_BASEMAP_USER_CHOSEN = 'siteTakeoffV2_basemap_userChosen';
const LS_KEY_OVERLAYS = 'siteTakeoffV2_overlays';
const LS_KEY_DATA_LAYERS = 'siteTakeoffV2_dataLayers';

/**
 * Hook that manages basemap provider + GIS overlays + data-layer toggles
 * for the site takeoff map. State persists to localStorage per user.
 */
export default function useMapLayers(mapRef, tileLayerRef) {
  // ===== Basemap state =====
  const [providers, setProviders] = useState([
    googleHybridProvider,
    googleSatelliteProvider,
    googleMapsProvider,
  ]);
  const [currentProviderName, setCurrentProviderName] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_KEY_BASEMAP);
      // Migration: if a prior selection exists from before we tracked user-chosen,
      // treat it as an explicit choice so we don't overwrite it when Maricopa loads.
      if (saved && !localStorage.getItem(LS_KEY_BASEMAP_USER_CHOSEN)) {
        localStorage.setItem(LS_KEY_BASEMAP_USER_CHOSEN, '1');
      }
      return saved || googleHybridProvider.name;
    } catch (_) {
      return googleHybridProvider.name;
    }
  });

  // ===== GIS overlay state =====
  const [activeOverlays, setActiveOverlays] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_KEY_OVERLAYS);
      return saved ? JSON.parse(saved) : ['streets'];
    } catch (_) {
      return ['streets'];
    }
  });
  const overlayLayersRef = useRef({}); // key -> leaflet layer

  // ===== Data-layer toggles (restricted areas, property lines) =====
  const [dataLayers, setDataLayers] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_KEY_DATA_LAYERS);
      return saved
        ? JSON.parse(saved)
        : { restrictedAreas: true, propertyLines: true, geofence: true, takeoffAreas: true };
    } catch (_) {
      return { restrictedAreas: true, propertyLines: true, geofence: true, takeoffAreas: true };
    }
  });

  // ===== Lazy-load Maricopa yearly aerial providers =====
  // Primary source: gis.mcassessor.maricopa.gov (assessor server) — uses
  // checkYearService() from mapUtils.js which probes Imagery/Aerials{year}.
  // This is what V1 uses and works for recent years (2024, 2025, 2026).
  // Fallback: gis.maricopa.gov/imagery (GIO server) for additional years
  // that have working tile endpoints (2021-2024 confirmed).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadEsriLeaflet();

        // Step 1: Use checkYearService for the assessor server (recent years)
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let y = currentYear + 1; y >= 2017; y--) years.push(y);
        const assessorResults = await Promise.all(years.map(y => checkYearService(y)));
        if (cancelled) return;

        const valid = assessorResults.filter(Boolean);

        // Step 2: For years not found on the assessor, try the GIO imagery server
        const foundYears = new Set(valid.map(p => p.year));
        const GIO_BASE = 'https://gis.maricopa.gov/imagery/rest/services';
        const gioFallbacks = [
          { year: 2023, name: 'Aerial2023Sep2023OctOrtho' },
          { year: 2022, name: 'Aerial2022Sep2022OctOrtho' },
          { year: 2021, name: 'Aerial2021Sep2021NovOrtho' },
        ].filter(svc => !foundYears.has(svc.year));

        const gioResults = await Promise.all(gioFallbacks.map(async (svc) => {
          try {
            const serviceUrl = `${GIO_BASE}/${svc.name}/MapServer`;
            // Quick tile probe — only add if tiles actually serve
            const probeResp = await fetch(`${serviceUrl}/tile/8/26304/12358`, {
              method: 'HEAD',
              signal: AbortSignal.timeout ? AbortSignal.timeout(5000) : undefined,
            });
            if (!probeResp.ok) return null;

            const metaResp = await fetch(`${serviceUrl}?f=json`, {
              signal: AbortSignal.timeout ? AbortSignal.timeout(8000) : undefined,
            });
            if (!metaResp.ok) return null;
            const data = await metaResp.json();
            if (!data.tileInfo?.lods?.length) return null;
            const maxLod = data.tileInfo.lods[data.tileInfo.lods.length - 1];

            return {
              year: svc.year,
              name: `Maricopa County ${svc.year}`,
              displayName: `Maricopa County (${svc.year})`,
              url: `${serviceUrl}/tile/{z}/{y}/{x}`,
              serviceUrl,
              attribution: `© Maricopa County (${svc.year})`,
              maxZoom: maxLod.level,
              minZoom: data.tileInfo.lods[0].level,
              isTiledService: true,
              isExportService: false,
              tilesVerified: true,
              group: 'maricopa',
            };
          } catch (_) {
            return null;
          }
        }));

        if (cancelled) return;
        const allValid = [...valid, ...gioResults.filter(Boolean)];

        // Sort by year descending
        allValid.sort((a, b) => (b.year || 0) - (a.year || 0));

        // Sort complete

        if (allValid.length > 0) {
          setProviders(prev => {
            const existingNames = new Set(prev.map(p => p.name));
            const toAdd = allValid.filter(p => !existingNames.has(p.name));
            return [...prev, ...toAdd];
          });
          console.log(`Loaded ${allValid.length} Maricopa aerial providers`);

          // If the user has never explicitly picked a basemap, default to the
          // most recent Maricopa County aerial. Google Hybrid stays as the
          // pre-load fallback so the map isn't blank during load.
          let userChosen = false;
          try { userChosen = localStorage.getItem(LS_KEY_BASEMAP_USER_CHOSEN) === '1'; } catch (_) {}
          if (!userChosen) {
            setCurrentProviderName(allValid[0].name);
          }
        }
      } catch (err) {
        console.warn('Failed to lazy-load Maricopa providers:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ===== Basemap swap: whenever currentProviderName changes, swap the tile layer =====
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const provider = providers.find(p => p.name === currentProviderName);
    if (!provider) return;

    // Remove existing tile layer
    if (tileLayerRef.current) {
      try { map.removeLayer(tileLayerRef.current); } catch (_) {}
    }

    let newLayer;
    if (provider.isExportService && window.L?.esri?.dynamicMapLayer) {
      // Dynamic/export service (rare — some very old years)
      newLayer = window.L.esri.dynamicMapLayer({
        url: provider.url,
        maxZoom: 28,
      });
    } else {
      // Standard L.tileLayer for everything else — Google AND Maricopa.
      // This matches V1 behavior exactly. Maricopa assessor tiles use
      // standard Web Mercator XYZ for 2023+ (works perfectly).
      // Older years may have broken tiles — errorTileUrl='' hides them.
      // maxNativeZoom capped at 21 enables digital zoom past that level.
      const nativeMax = Math.min(provider.maxZoom || 20, 21);
      newLayer = L.tileLayer(provider.url, {
        attribution: provider.attribution || '',
        maxNativeZoom: nativeMax,
        maxZoom: 28,
        errorTileUrl: '',
      });
    }
    newLayer.addTo(map);
    // Push basemap to the bottom of the layer stack
    if (newLayer.setZIndex) newLayer.setZIndex(0);
    tileLayerRef.current = newLayer;

    // Force the map to re-evaluate tile loading at the current zoom
    setTimeout(() => {
      try { map.invalidateSize(); } catch (_) {}
    }, 100);

    // Persist selection
    try {
      localStorage.setItem(LS_KEY_BASEMAP, currentProviderName);
    } catch (_) {}
  }, [currentProviderName, providers, mapRef, tileLayerRef]);

  // ===== Sync GIS overlays to the map =====
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const active = new Set(activeOverlays);

    // Remove overlays no longer active
    Object.keys(overlayLayersRef.current).forEach(key => {
      if (!active.has(key)) {
        const layer = overlayLayersRef.current[key];
        try { map.removeLayer(layer); } catch (_) {}
        delete overlayLayersRef.current[key];
      }
    });

    // Add newly-active overlays
    active.forEach(key => {
      if (overlayLayersRef.current[key]) return; // already added
      const def = GIS_OVERLAYS.find(o => o.key === key);
      if (!def) return;

      let layer;
      try {
        if (def.type === 'feature' && window.L?.esri?.featureLayer) {
          layer = window.L.esri.featureLayer({
            url: def.url,
            style: () => def.style || { color: '#FF3D00', weight: 2, fillOpacity: 0 },
          }).addTo(map);
        } else if (def.type === 'dynamic' && window.L?.esri?.dynamicMapLayer) {
          layer = window.L.esri.dynamicMapLayer({
            url: def.url,
            opacity: def.opacity || 0.6,
          }).addTo(map);
        } else if (def.type === 'tile') {
          layer = L.tileLayer(def.url, {
            opacity: def.opacity || 0.5,
            maxNativeZoom: def.maxZoom || 20,
            maxZoom: 28,
            errorTileUrl: '',
          }).addTo(map);
        }
      } catch (err) {
        console.warn(`Failed to add GIS overlay ${key}:`, err);
        return;
      }

      if (layer) overlayLayersRef.current[key] = layer;
    });

    // Persist selection
    try {
      localStorage.setItem(LS_KEY_OVERLAYS, JSON.stringify(activeOverlays));
    } catch (_) {}
  }, [activeOverlays, mapRef]);

  // ===== Persist data layer toggles =====
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY_DATA_LAYERS, JSON.stringify(dataLayers));
    } catch (_) {}
  }, [dataLayers]);

  // ===== Handlers =====
  const selectBasemap = useCallback((providerName) => {
    setCurrentProviderName(providerName);
    try { localStorage.setItem(LS_KEY_BASEMAP_USER_CHOSEN, '1'); } catch (_) {}
  }, []);

  const toggleOverlay = useCallback((key) => {
    setActiveOverlays(prev => {
      if (prev.includes(key)) return prev.filter(k => k !== key);
      return [...prev, key];
    });
  }, []);

  const toggleDataLayer = useCallback((key) => {
    setDataLayers(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  return {
    providers,
    currentProviderName,
    selectBasemap,
    activeOverlays,
    toggleOverlay,
    dataLayers,
    toggleDataLayer,
  };
}
